<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="pageTitle" :text="breadcrumbRoot" :text1="breadcrumbLeaf" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap gap-2 dq-page-actions">
          <div class="me-2 mb-2">
            <label class="form-label mb-0 me-1">Period</label>
            <input
              v-model="filters.period"
              type="month"
              class="form-control"
              @change="fetchReport"
              style="min-width: 180px"
            />
          </div>

          <div class="mb-2">
            <button class="btn btn-white" @click="fetchReport" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Refresh
            </button>
          </div>

          <div class="mb-2">
            <button
              class="btn btn-dark"
              @click="downloadTemplateFile"
              :disabled="loading"
              v-can="'Reports.Export'"
            >
              <i class="ti ti-file-export me-1"></i>
              Download {{ exportLabel }}
            </button>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <div class="row mb-3 dq-stat-grid">
        <div class="col-md-3 d-flex" v-for="card in kpiCards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>
                    <span v-if="card.currency">
                      {{ currency }}
                      {{
                        Number(card.value || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      }}
                    </span>
                    <span v-else>
                      {{ card.value || 0 }}
                    </span>
                  </h5>
                </div>
                <a
                  href="javascript:void(0);"
                  class="avatar avatar-md avatar-rounded"
                  :class="`bg-transparent-${card.cls} border border-${card.cls}`"
                >
                  <span :class="`text-${card.cls}`">
                    <i :class="card.icon"></i>
                  </span>
                </a>
              </div>
              <p class="fs-12 text-muted mb-0">
                Due on {{ dueDateLabel }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-2">
          <div>
            <h5 class="mb-1">{{ pageTitle }}</h5>
            <p class="text-muted mb-0">Template structure: {{ templateColumnsText }}</p>
          </div>
        </div>
        <div class="card-body p-0">
          <div v-if="error" class="alert alert-danger m-3 mb-0">{{ error }}</div>

          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light dq-report-table"
              :columns="columns"
              :data-source="dataSource"
              :loading="loading"
              :pagination="false"
              row-key="key"
              size="small"
            >
              <template #bodyCell="{ column, record }">
                <template v-if="moneyFields.includes(column.dataIndex)">
                  {{ currency }}
                  {{
                    Number(record[column.dataIndex] || 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })
                  }}
                </template>
              </template>
            </a-table>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import moment from "moment";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

const route = useRoute();

const filters = reactive({
  period: moment().format("YYYY-MM"),
});

const loading = ref(false);
const error = ref("");
const summary = ref(null);
const rows = ref([]);
const template = ref(null);

const reportType = computed(() => route.meta?.complianceType || "paye");
const pageTitle = computed(() => route.meta?.reportTitle || "Monthly Compliance Report");
const breadcrumbRoot = "Administration";
const breadcrumbLeaf = computed(() => `Reports / ${route.meta?.reportGroup || "Monthly Reports"}`);
const currency = computed(() => summary.value?.currency || "KES");
const dueDateLabel = computed(() =>
  summary.value?.submission_due_date
    ? moment(summary.value.submission_due_date).format("DD MMM YYYY")
    : "-"
);
const templateColumnsText = computed(() => {
  const cols = template.value?.column_count || 0;
  return template.value?.has_headers ? `${cols} columns with headers` : `${cols} columns without headers`;
});
const templateHint = computed(() =>
  template.value?.has_headers ? "header row retained from template" : ""
);
const exportLabel = computed(() => {
  const ext = template.value?.format?.toUpperCase?.() || "FILE";
  return `${pageTitle.value} (${ext})`;
});

const moneyFields = [
  "gross_pay",
  "taxable_income",
  "income_tax",
  "personal_relief",
  "paye",
  "nssf",
  "shif",
  "ahl",
  "contribution_amount",
];

const columns = computed(() => {
  switch (reportType.value) {
    case "nssf":
      return [
        { title: "Payroll Number", dataIndex: "employee_id", key: "employee_id" },
        { title: "Surname", dataIndex: "last_name", key: "last_name" },
        { title: "Other Names", dataIndex: "other_names", key: "other_names" },
        { title: "ID No", dataIndex: "id_number", key: "id_number" },
        { title: "KRA PIN", dataIndex: "kra_pin", key: "kra_pin" },
        { title: "NSSF No", dataIndex: "nssf_number", key: "nssf_number" },
        { title: "Gross Pay", dataIndex: "gross_pay", key: "gross_pay" },
        { title: "Voluntary", dataIndex: "voluntary", key: "voluntary" },
      ];
    case "shif":
      return [
        { title: "Payroll Number", dataIndex: "employee_id", key: "employee_id" },
        { title: "First Name", dataIndex: "first_name", key: "first_name" },
        { title: "Last Name", dataIndex: "last_name", key: "last_name" },
        { title: "Identity Type", dataIndex: "identity_type", key: "identity_type" },
        { title: "ID No", dataIndex: "id_number", key: "id_number" },
        { title: "KRA PIN", dataIndex: "kra_pin", key: "kra_pin" },
        { title: "NHIF/SHIF No", dataIndex: "nhif_number", key: "nhif_number" },
        { title: "Contribution Amount", dataIndex: "contribution_amount", key: "contribution_amount" },
        { title: "Phone", dataIndex: "phone", key: "phone" },
      ];
    case "ahl":
      return [
        { title: "ID No", dataIndex: "id_number", key: "id_number" },
        { title: "Employee Name", dataIndex: "full_name", key: "full_name" },
        { title: "KRA PIN", dataIndex: "kra_pin", key: "kra_pin" },
        { title: "Gross Pay", dataIndex: "gross_pay", key: "gross_pay" },
      ];
    default:
      return [
        { title: "KRA PIN", dataIndex: "kra_pin", key: "kra_pin" },
        { title: "Employee Name", dataIndex: "full_name", key: "full_name" },
        { title: "Gross Pay", dataIndex: "gross_pay", key: "gross_pay" },
        { title: "Taxable Pay", dataIndex: "taxable_income", key: "taxable_income" },
        { title: "Tax Charged", dataIndex: "income_tax", key: "income_tax" },
        { title: "Personal Relief", dataIndex: "personal_relief", key: "personal_relief" },
        { title: "PAYE", dataIndex: "paye", key: "paye" },
      ];
  }
});

const dataSource = computed(() =>
  (rows.value || []).map((row, index) => ({
    key: `${reportType.value}-${index}`,
    ...row,
  }))
);

const kpiCards = computed(() => {
  if (reportType.value === "nssf") {
    return [
      { title: "Employees", value: summary.value?.employee_count || 0, cls: "primary", icon: "ti ti-users", currency: false },
      { title: "Total Gross", value: summary.value?.total_gross || 0, cls: "info", icon: "ti ti-cash-banknote", currency: true },
      { title: "Total NSSF", value: summary.value?.total_nssf || 0, cls: "danger", icon: "ti ti-building-bank", currency: true },
      { title: "Period", value: summary.value?.period_label || "-", cls: "warning", icon: "ti ti-calendar", currency: false },
    ];
  }

  if (reportType.value === "shif") {
    return [
      { title: "Employees", value: summary.value?.employee_count || 0, cls: "primary", icon: "ti ti-users", currency: false },
      { title: "Total Gross", value: summary.value?.total_gross || 0, cls: "info", icon: "ti ti-cash-banknote", currency: true },
      { title: "Total SHIF", value: summary.value?.total_shif || 0, cls: "warning", icon: "ti ti-heart-rate-monitor", currency: true },
      { title: "Period", value: summary.value?.period_label || "-", cls: "danger", icon: "ti ti-calendar", currency: false },
    ];
  }

  if (reportType.value === "ahl") {
    return [
      { title: "Employees", value: summary.value?.employee_count || 0, cls: "primary", icon: "ti ti-users", currency: false },
      { title: "Total Gross", value: summary.value?.total_gross || 0, cls: "info", icon: "ti ti-cash-banknote", currency: true },
      { title: "Total AHL", value: summary.value?.total_ahl || 0, cls: "warning", icon: "ti ti-home", currency: true },
      { title: "Period", value: summary.value?.period_label || "-", cls: "danger", icon: "ti ti-calendar", currency: false },
    ];
  }

  return [
    { title: "Employees", value: summary.value?.employee_count || 0, cls: "primary", icon: "ti ti-users", currency: false },
    { title: "Total Gross", value: summary.value?.total_gross || 0, cls: "info", icon: "ti ti-cash-banknote", currency: true },
    { title: "Total Taxable", value: summary.value?.total_taxable_income || 0, cls: "warning", icon: "ti ti-report-money", currency: true },
    { title: "Total PAYE", value: summary.value?.total_paye || 0, cls: "danger", icon: "ti ti-receipt-tax", currency: true },
  ];
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function fetchReport() {
  if (!filters.period) return;

  loading.value = true;
  error.value = "";

  try {
    const { data } = await reportsService.monthlyCompliance({
      period: filters.period,
      type: reportType.value,
    });

    summary.value = data.summary || null;
    rows.value = data.rows || [];
    template.value = data.template || null;
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || "Failed to load monthly compliance report.";
    toast(error.value, "error");
  } finally {
    loading.value = false;
  }
}

function filenameFromDisposition(headers, fallback) {
  const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

async function downloadTemplateFile() {
  try {
    const response = await reportsService.exportMonthlyCompliance({
      period: filters.period,
      type: reportType.value,
    });

    const fallbackName = template.value?.filename || `${reportType.value}-${filters.period}`;
    const filename = filenameFromDisposition(response.headers, fallbackName);
    const blob = new Blob([response.data], {
      type: response.headers?.["content-type"] || "application/octet-stream",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast(err?.response?.data?.message || err?.message || "Failed to download export.", "error");
  }
}

watch(reportType, () => {
  fetchReport();
});

onMounted(fetchReport);
</script>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: stretch !important;
  }

  .dq-page-actions > * {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .dq-page-actions .form-control,
  .dq-page-actions .btn {
    width: 100%;
  }

  :deep(.dq-report-table .ant-table) {
    min-width: 920px;
  }
}
</style>


